import React, { useState } from "react";
import { createPortal } from "react-dom";

// Save-to-list picker for the profile-header "Add to Radar" button.
// Presentational: the parent (AddToRadarButton) owns radar data + the draft of
// checked list ids; this renders it. Two presentations, both pixel-matched to
// the TP-Radar prototype (desktop: 250px anchored popover; mobile: bottom sheet
// with ≥44px touch targets). Toggles mutate the parent's draft; Done/close
// commits once (parent → save-from-profile).

const checkboxStyle = (checked, mobile) => ({
  width: mobile ? "22px" : "16px",
  height: mobile ? "22px" : "16px",
  borderRadius: mobile ? "6px" : "5px",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: mobile ? "12px" : "10px",
  fontWeight: 800,
  color: "#FFFFFF",
  background: checked ? "#2283FF" : "#FFFFFF",
  border: checked ? "1px solid #2283FF" : "1px solid #C7D0DC",
});

// One checkbox row (list). Shared between popover + sheet, sized per platform.
const ListRow = ({ list, checked, onToggle, mobile }) => (
  <div
    onClick={(e) => {
      e.stopPropagation();
      onToggle(list.id);
    }}
    role="checkbox"
    aria-checked={checked}
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggle(list.id);
      }
    }}
    style={
      mobile
        ? {
            display: "flex",
            alignItems: "center",
            gap: "11px",
            minHeight: "52px",
            cursor: "pointer",
          }
        : {
            display: "flex",
            alignItems: "center",
            gap: "9px",
            padding: "7px 8px",
            borderRadius: "7px",
            cursor: "pointer",
          }
    }
  >
    <div style={checkboxStyle(checked, mobile)}>{checked ? "✓" : ""}</div>
    <div
      style={{
        flex: 1,
        fontSize: mobile ? "14.5px" : "12.5px",
        fontWeight: 600,
        color: "#374151",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {list.name}
    </div>
  </div>
);

const SaveToListPicker = ({
  isMobile,
  lists,
  draftListIds,
  onToggle,
  onCreateList,
  creating,
  onDone,
}) => {
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const stop = (e) => e.stopPropagation();

  const submitNew = async () => {
    const v = newName.trim();
    if (!v) {
      setNewOpen(false);
      return;
    }
    const ok = await onCreateList(v);
    if (ok) {
      setNewName("");
      setNewOpen(false);
    }
  };

  const newListInput = (mobile) => (
    <div
      style={
        mobile
          ? { display: "flex", gap: "8px", padding: "6px 0 4px" }
          : { display: "flex", gap: "6px", padding: "6px 4px 4px" }
      }
    >
      <input
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submitNew();
        }}
        autoFocus
        placeholder="List name"
        maxLength={60}
        style={{
          flex: 1,
          minWidth: 0,
          height: mobile ? "44px" : "30px",
          border: "1px solid #DDE3EB",
          borderRadius: mobile ? "9px" : "8px",
          padding: mobile ? "0 11px" : "0 8px",
          fontSize: mobile ? "13.5px" : "12.5px",
          outline: "none",
        }}
      />
      <button
        type="button"
        onClick={submitNew}
        disabled={creating}
        style={{
          height: mobile ? "44px" : "auto",
          background: "#2283FF",
          color: "#FFFFFF",
          border: "none",
          borderRadius: mobile ? "9px" : "8px",
          padding: mobile ? "0 15px" : "0 11px",
          fontSize: mobile ? "13px" : "12px",
          fontWeight: 600,
          cursor: creating ? "default" : "pointer",
          opacity: creating ? 0.6 : 1,
        }}
      >
        Add
      </button>
    </div>
  );

  // ── Mobile: bottom sheet (portal to body, escapes the header's border box) ──
  if (isMobile) {
    return createPortal(
      <div
        onClick={onDone}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 60,
          background: "rgba(17,24,39,0.45)",
          display: "flex",
          alignItems: "flex-end",
        }}
      >
        <div
          onClick={stop}
          style={{
            width: "100%",
            background: "#FFFFFF",
            borderRadius: "18px 18px 0 0",
            padding: "8px 16px 40px",
            animation: "tp-sheet 0.2s ease",
          }}
        >
          <div
            style={{
              width: "36px",
              height: "4px",
              borderRadius: "2px",
              background: "#DDE3EB",
              margin: "4px auto 14px",
            }}
          />
          <div
            style={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#111827",
              marginBottom: "4px",
            }}
          >
            Save to list
          </div>
          {lists.map((l) => (
            <ListRow
              key={l.id}
              list={l}
              checked={draftListIds.includes(l.id)}
              onToggle={onToggle}
              mobile
            />
          ))}
          {newOpen ? (
            newListInput(true)
          ) : (
            <button
              type="button"
              onClick={() => setNewOpen(true)}
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                minHeight: "50px",
                textAlign: "left",
                background: "none",
                border: "none",
                padding: 0,
                fontSize: "14px",
                color: "#2283FF",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              + New list
            </button>
          )}
          <button
            type="button"
            onClick={onDone}
            style={{
              width: "100%",
              height: "48px",
              marginTop: "10px",
              background: "#2283FF",
              color: "#FFFFFF",
              border: "none",
              borderRadius: "9px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </div>,
      document.body
    );
  }

  // ── Desktop: 250px popover anchored to the button (parent is position:relative) ──
  return (
    <div
      onClick={stop}
      style={{
        position: "absolute",
        right: 0,
        top: "46px",
        zIndex: 30,
        width: "250px",
        background: "#FFFFFF",
        border: "1px solid #E4E9F0",
        borderRadius: "11px",
        boxShadow: "0 12px 32px rgba(17,24,39,0.14)",
        padding: "8px",
        animation: "tp-pop 0.12s ease",
      }}
    >
      <div
        style={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "#9CA3AF",
          padding: "5px 8px 6px",
        }}
      >
        Save to list
      </div>
      {lists.map((l) => (
        <ListRow
          key={l.id}
          list={l}
          checked={draftListIds.includes(l.id)}
          onToggle={onToggle}
          mobile={false}
        />
      ))}
      {newOpen ? (
        newListInput(false)
      ) : (
        <button
          type="button"
          onClick={() => setNewOpen(true)}
          style={{
            display: "block",
            width: "100%",
            textAlign: "left",
            background: "none",
            border: "none",
            padding: "8px",
            borderRadius: "7px",
            fontSize: "12.5px",
            color: "#2283FF",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          + New list
        </button>
      )}
      <div style={{ padding: "6px 4px 2px" }}>
        <button
          type="button"
          onClick={onDone}
          style={{
            width: "100%",
            background: "#2283FF",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "8px",
            padding: "8px 0",
            fontSize: "12.5px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default SaveToListPicker;
